import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBudgetAssistantStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Car, Coffee, Gamepad2, ShoppingBag, Info, TrendingUp } from 'lucide-react'

interface VariableExpensesStepProps {
  onNext: () => void
  onSkip: () => void
}

interface ExpenseItem {
  id: string
  label: string
  icon: React.ReactNode
  description: string
  amount: string
}

export function VariableExpensesStep({ onNext, onSkip }: VariableExpensesStepProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { responses, updateResponses } = useBudgetAssistantStore()
  
  const [expenses, setExpenses] = useState<ExpenseItem[]>([
    {
      id: 'transportation',
      label: t('budgetAssistant.variableExpenses.transportation.question', 'How much do you spend on transportation?'),
      icon: <Car className="h-5 w-5 text-blue-600" />,
      description: t('budgetAssistant.variableExpenses.transportation.description', 'Gas, public transport, ride-sharing, parking fees, or car maintenance'),
      amount: ''
    },
    {
      id: 'diningOut',
      label: t('budgetAssistant.variableExpenses.dining.question', 'How much do you spend on dining out or cafes?'),
      icon: <Coffee className="h-5 w-5 text-orange-600" />,
      description: t('budgetAssistant.variableExpenses.dining.description', 'Restaurants, coffee shops, takeout, delivery, or treats when you\'re out'),
      amount: ''
    },
    {
      id: 'entertainment',
      label: t('budgetAssistant.variableExpenses.entertainment.question', 'What\'s your average spend on entertainment or subscriptions?'),
      icon: <Gamepad2 className="h-5 w-5 text-purple-600" />,
      description: t('budgetAssistant.variableExpenses.entertainment.description', 'Netflix, Spotify, movies, hobbies, gym memberships, or fun activities'),
      amount: ''
    },
    {
      id: 'shopping',
      label: t('budgetAssistant.variableExpenses.shopping.question', 'How much do you typically spend on clothes or non-essentials?'),
      icon: <ShoppingBag className="h-5 w-5 text-pink-600" />,
      description: t('budgetAssistant.variableExpenses.shopping.description', 'Clothing, electronics, home goods, books, or impulse purchases'),
      amount: ''
    }
  ])

  const [isLoading, setIsLoading] = useState(false)

  // Load existing responses into local state
  useEffect(() => {
    setExpenses(prevExpenses => prevExpenses.map(expense => {
      const savedAmount = responses[expense.id as keyof typeof responses] as number
      return {
        ...expense,
        amount: savedAmount ? savedAmount.toString() : ''
      }
    }))
  }, [responses])

  const updateExpenseAmount = (id: string, amount: string) => {
    setExpenses(prev => prev.map(expense => 
      expense.id === id ? { ...expense, amount } : expense
    ))
  }

  const handleNext = async () => {
    if (user?.id) {
      setIsLoading(true)
      try {
        const expenseData = expenses.reduce((acc, expense) => {
          if (expense.amount && parseFloat(expense.amount) > 0) {
            acc[expense.id as keyof typeof acc] = parseFloat(expense.amount)
          }
          return acc
        }, {} as Record<string, number>)
        
        await updateResponses(expenseData, 'variable_expenses')
        onNext()
      } catch (error) {
        console.error('Error saving variable expenses data:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const getTotalExpenses = () => {
    return expenses.reduce((total, expense) => {
      const amount = parseFloat(expense.amount) || 0
      return total + amount
    }, 0)
  }

  const totalExpenses = getTotalExpenses()

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="relative mx-auto w-14 h-14 mb-3">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl p-3 shadow-lg">
            <Coffee className="h-8 w-8 text-white" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('budgetAssistant.variableExpenses.title', 'Now for the fun stuff - your flexible spending')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('budgetAssistant.variableExpenses.subtitle', 'These expenses can change from month to month. Think about your average spending in each category.')}
        </p>
      </div>

      {/* Expenses Form */}
      <div className="space-y-3">
        {expenses.map((expense) => (
          <Card key={expense.id} className="p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
                {expense.icon}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {expense.label}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {expense.description}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      $
                    </div>
                    <Input
                      id={expense.id}
                      type="number"
                      placeholder="0.00"
                      value={expense.amount}
                      onChange={(e) => updateExpenseAmount(expense.id, e.target.value)}
                      className="pl-8 h-10 text-sm w-32"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <span className="text-xs text-gray-500">per month</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Total Summary */}
      {totalExpenses > 0 && (
        <Card className="p-5 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-purple-900 dark:text-purple-100 text-sm">
                {t('budgetAssistant.variableExpenses.total.title', 'Your flexible spending totals:')}
              </h4>
              <p className="text-xs text-purple-700 dark:text-purple-300">
                {t('budgetAssistant.variableExpenses.total.description', 'This is what you spend on lifestyle and entertainment')}
              </p>
            </div>
            <div className="text-xl font-bold text-purple-800 dark:text-purple-200">
              ${totalExpenses.toLocaleString()}
            </div>
          </div>
        </Card>
      )}

      {/* Budget Tips */}
      <Card className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200 dark:border-emerald-800">
        <div className="flex items-center gap-3 mb-3">
          <TrendingUp className="h-4 w-4 text-emerald-600" />
          <h4 className="font-medium text-emerald-900 dark:text-emerald-100 text-sm">
            {t('budgetAssistant.variableExpenses.tips.title', 'Smart Spending Tips')}
          </h4>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-emerald-700 dark:text-emerald-300">
            • {t('budgetAssistant.variableExpenses.tips.tip1', 'These are the easiest expenses to adjust if you need to save more money')}
          </p>
          <p className="text-xs text-emerald-700 dark:text-emerald-300">
            • {t('budgetAssistant.variableExpenses.tips.tip2', 'Consider the 50/30/20 rule: 30% of income can go to wants and entertainment')}
          </p>
          <p className="text-xs text-emerald-700 dark:text-emerald-300">
            • {t('budgetAssistant.variableExpenses.tips.tip3', 'Track these for a month to get a better average if you\'re unsure')}
          </p>
        </div>
      </Card>

      {/* Help Card */}
      <Card className="p-4 bg-gray-50/50 dark:bg-gray-950/20 border-gray-200 dark:border-gray-800">
        <div className="flex items-start gap-3">
          <Info className="h-4 w-4 text-gray-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1 text-sm">
              {t('budgetAssistant.variableExpenses.help.title', 'Remember')}
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t('budgetAssistant.variableExpenses.help.description', 'These amounts can vary month to month - just give us your best estimate of what you typically spend.')}
            </p>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={onSkip}
          className="text-gray-600 border-gray-300 hover:bg-gray-50 h-10"
        >
          {t('common.skip', 'Skip')}
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={isLoading}
          className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-6 h-10"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            t('common.continue', 'Continue')
          )}
        </Button>
      </div>
    </div>
  )
} 